import React, { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { MagnifyingGlassAiIcon } from '@/utils/icons';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';

interface VideoCropperActionsPopoverProps {
    style: React.CSSProperties;
    isDownloading: boolean;
    isAttachingToQuery: boolean;
    onDownload: () => void;
    onAttachToQuery: () => void;
}

export const VideoCropperActionsPopover = forwardRef<
    HTMLDivElement,
    VideoCropperActionsPopoverProps
>(
    (
        {
            style,
            isDownloading,
            isAttachingToQuery,
            onDownload,
            onAttachToQuery,
        },
        ref,
    ) => {
        const { t } = useTranslation(I18nNamespace.VIDEO);
        const actionsBusy = isDownloading || isAttachingToQuery;

        return createPortal(
            <div
                ref={ref}
                className="fixed z-[100] flex items-center gap-1 rounded-lg border border-grey-500 bg-grey-600 p-1 shadow-[0_4px_48px_-4px_rgba(7,8,10,0.5)]"
                style={style}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <button
                                type="button"
                                onClick={onAttachToQuery}
                                disabled={actionsBusy}
                                aria-label={t('crop.attachToQuery')}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-white-100 hover:bg-surface-hover-strong disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                            >
                                <MagnifyingGlassAiIcon className="size-5" />
                            </button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>{t('crop.attachToQuery')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <button
                                type="button"
                                onClick={onDownload}
                                disabled={actionsBusy}
                                aria-label={t('crop.downloadImage')}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-white-100 hover:bg-surface-hover-strong disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                            >
                                <DownloadSimpleIcon className="size-5" />
                            </button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>{t('crop.downloadImage')}</TooltipContent>
                </Tooltip>
            </div>,
            document.body,
        );
    },
);

VideoCropperActionsPopover.displayName = 'VideoCropperActionsPopover';
